import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { GradientCheckbox } from '../filters/GradientCheckbox';
import type { SortConfig, SortOption } from '../../../contexts/DynamicHeaderContext';

interface SortDropdownProps {
  options: SortOption[];
  current?: SortConfig;
  onChange?: (sort: SortConfig) => void;
}

export function SortDropdown({ options, current, onChange }: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption = options.find(o => o.field === current?.field) || options[0];

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 224;
      const dropdownHeight = 250;
      const spaceBelow = window.innerHeight - rect.bottom;
      let top = rect.bottom + 4;
      if (spaceBelow < dropdownHeight && rect.top > spaceBelow) {
        top = rect.top - dropdownHeight - 4;
      }
      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;
      if (spaceRight < dropdownWidth + 16) left = rect.right - dropdownWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));
      setPosition({ top: Math.max(8, top), left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const handleSelect = (field: string) => {
    onChange?.({ field, direction: current?.direction || 'asc' });
    setIsOpen(false);
  };

  const toggleDirection = () => {
    const field = current?.field || options[0]?.field || 'custom';
    const newDirection = current?.direction === 'asc' ? 'desc' : 'asc';
    onChange?.({ field, direction: newDirection });
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="inline-flex items-center gap-1 px-2.5 h-7 text-xs rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          <ArrowUpDown className="w-3 h-3" />
          <span className="truncate max-w-[120px]">{currentOption?.label}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <button
          onClick={toggleDirection}
          className="flex items-center justify-center w-7 h-7 text-xs rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          title={current?.direction === 'asc' ? 'Crescente' : 'Decrescente'}
        >
          {current?.direction === 'desc' ? (
            <ArrowDown className="w-3 h-3" />
          ) : (
            <ArrowUp className="w-3 h-3" />
          )}
        </button>
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-56 max-h-64 rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col overflow-hidden"
          style={{ top: position.top, left: position.left }}
        >
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Ordina per</span>
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            {options.map(option => {
              const isSelected = current?.field === option.field;
              return (
                <div
                  key={option.field}
                  onClick={() => handleSelect(option.field)}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded hover:bg-gray-50 dark:hover:bg-white/10 cursor-pointer"
                >
                  <GradientCheckbox checked={isSelected} />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{option.label}</span>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
